import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { ConfigClient, ConfigCollection } from '@atlassian/js-config-client-beta';
import { environment, envs } from '../../services/environment';

interface FeatureFlags {
  'pc-frontend-down-for-maintenance': boolean;
  'pc-frontend-use-sso': boolean;
}

const defaultFeatureFlags: FeatureFlags = {
  'pc-frontend-down-for-maintenance': false,
  'pc-frontend-use-sso': false,
};

const FeatureFlagProviderContext = React.createContext<FeatureFlags>(defaultFeatureFlags);

const getFfsConfig = (): { apiKey: string; baseUrl: string } => {
  switch (environment().name) {
    case envs.STAGE:
      return { apiKey: '4fe8b2e3-2652-4c3b-ae74-a68f018b02c1', baseUrl: 'https://api.stg.atlassian.com/flags' };
    case envs.PROD:
      return { apiKey: 'f7e6ffcd-c5c4-476d-b870-5fe4dc7903e8', baseUrl: 'https://api.atlassian.com/flags' };
    default:
      return { apiKey: '3d7da3c9-0e4a-4e84-bb50-0e85faeb1f11', baseUrl: 'https://api.dev.atlassian.com/flags' };
  }
};

const readBoolean = (configCollection: ConfigCollection, flag: string, defaultValue: boolean): boolean => {
  const configValue = configCollection.getBoolean(flag);
  if (!configValue || 'error' in configValue) {
    return defaultValue;
  }
  return configValue.value;
};

export const useFeatureFlags = () => useContext(FeatureFlagProviderContext);

export const FeatureFlagProvider = (props: { children: ReactNode }) => {
  const [featureFlagsLoaded, setFeatureFlagsLoaded] = useState(false);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(defaultFeatureFlags);

  useEffect(() => {
    async function fetchConfig() {
      try {
        const ffsConfig = getFfsConfig();
        const config = await ConfigClient.fetch({
          ffsBaseUrl: ffsConfig.baseUrl,
          ffsApiKey: ffsConfig.apiKey,
          context: {
            namespace: 'atl-preferences-center_web',
            identifiers: {
              randomizationId: crypto.randomUUID(),
            },
            metadata: {},
          },
        });
        setFeatureFlags({
          ['pc-frontend-down-for-maintenance']: readBoolean(
            config,
            'pc-frontend-down-for-maintenance',
            defaultFeatureFlags['pc-frontend-down-for-maintenance'],
          ),
          ['pc-frontend-use-sso']: readBoolean(
            config,
            'pc-frontend-use-sso',
            defaultFeatureFlags['pc-frontend-use-sso'],
          ),
        });
      } catch (e) {
        console.error('Failed to fetch feature flags', e);
      }
      setFeatureFlagsLoaded(true);
    }
    void fetchConfig();
  }, []);

  if (!featureFlagsLoaded) {
    return <></>; // show blank page while feature flags are loading
  }

  return (
    <FeatureFlagProviderContext.Provider value={featureFlags}>{props.children}</FeatureFlagProviderContext.Provider>
  );
};
